// Section 22 Build tab: the build queue with cost, materials on hand versus needed (shortfalls
// named in words as well as styled), seasons to completion at the current builder count, and
// move up/down (buttons, or Alt+ArrowUp/ArrowDown on a focused row), pause and cancel with the
// refund it would bring. Props only.
import type { BuildOrderId, Resource } from '../../sim/api';
import type { CancelledRow, QueueRow } from '../../viewModels/build';
import { buildText, resourceLabel } from '../text/en';
import { Explain } from './explain';
import { amount, materialsText, type Materials } from './materials';

interface Props {
  rows: readonly QueueRow[];
  cancelled: readonly CancelledRow[];
  builders: number;
  pointsPerSeason: number;
  stock: Materials;
  canEdit: boolean;
  onMove: (id: BuildOrderId, toIndex: number) => void;
  onPause: (id: BuildOrderId, paused: boolean) => void;
  onCancel: (id: BuildOrderId) => void;
  onRemove: (pendingIndex: number) => void;
}

function MaterialsCell({ row, stock }: { row: QueueRow; stock: Materials }) {
  if (row.materialsPaid) return <span class="hint">{buildText.paid}</span>;
  const entries = Object.entries(row.materials) as [Resource, number][];
  return (
    <ul class="build__materials">
      {entries.map(([r, need]) => {
        const short = row.shortfall[r];
        return (
          <li key={r} class={short === undefined ? undefined : 'is-short'}>
            <span class="num">
              {amount(stock[r] ?? 0)} / {amount(need)}
            </span>{' '}
            {resourceLabel[r].toLowerCase()}
            {short !== undefined && (
              <strong class="build__short"> {buildText.short(amount(short))}</strong>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function refundOf(row: QueueRow): string {
  return row.materialsPaid ? buildText.refund(materialsText(row.refund)) : buildText.cancelNoRefund;
}

export function BuildQueue(props: Props) {
  const { rows, cancelled, builders, pointsPerSeason, stock, canEdit } = props;
  const move = (row: QueueRow, by: number): void => {
    if (row.id !== null) props.onMove(row.id, row.index + by);
  };
  const onKey = (row: QueueRow, e: KeyboardEvent): void => {
    if (!canEdit || !e.altKey) return;
    if (e.key === 'ArrowUp' && row.canMoveUp) {
      e.preventDefault();
      move(row, -1);
    } else if (e.key === 'ArrowDown' && row.canMoveDown) {
      e.preventDefault();
      move(row, 1);
    }
  };
  return (
    <div class="build__queue">
      <p class="hint">
        {builders > 0
          ? buildText.forecast(builders, amount(pointsPerSeason))
          : buildText.noBuilders}
      </p>
      {rows.length === 0 ? (
        <p class="hint">{buildText.queueEmpty}</p>
      ) : (
        <>
          <p class="hint">{buildText.moveHint}</p>
          <table class="table">
            <thead>
              <tr>
                <th scope="col">{buildText.colPosition}</th>
                <th scope="col">{buildText.colBuilding}</th>
                <th scope="col">{buildText.colProgress}</th>
                <th scope="col">{buildText.colMaterials}</th>
                <th scope="col">{buildText.colSeasons}</th>
                <th scope="col">{buildText.colStatus}</th>
                <th scope="col">{buildText.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const label = row.repairOf === null ? row.label : buildText.repairOf(row.label);
                return (
                  <tr
                    key={row.id ?? `pending-${row.pendingIndex ?? row.index}`}
                    class={`build__order is-${row.status}`}
                    tabIndex={row.id === null ? undefined : 0}
                    onKeyDown={(e) => onKey(row, e)}
                  >
                    <td class="num">{row.index + 1}</td>
                    <th scope="row">
                      {label}
                      {row.pending && <span class="pending"> ({buildText.pending})</span>}
                    </th>
                    <td>
                      <span class="num">{amount(row.progress)}</span> /{' '}
                      <Explain
                        value={amount(row.required)}
                        factors={row.costFactors}
                        label={`${buildText.colCost}: ${label}`}
                      />
                    </td>
                    <td>
                      <MaterialsCell row={row} stock={stock} />
                    </td>
                    <td>{buildText.seasons(row.seasons)}</td>
                    <td>{buildText.status[row.status]}</td>
                    <td class="build__actions">
                      {row.id === null ? (
                        <button
                          type="button"
                          class="button button--small"
                          disabled={!canEdit}
                          onClick={() =>
                            row.pendingIndex !== null && props.onRemove(row.pendingIndex)
                          }
                        >
                          {buildText.remove}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            class="button button--small"
                            aria-label={buildText.moveUpLabel(label)}
                            disabled={!canEdit || !row.canMoveUp}
                            onClick={() => move(row, -1)}
                          >
                            {buildText.moveUp}
                          </button>
                          <button
                            type="button"
                            class="button button--small"
                            aria-label={buildText.moveDownLabel(label)}
                            disabled={!canEdit || !row.canMoveDown}
                            onClick={() => move(row, 1)}
                          >
                            {buildText.moveDown}
                          </button>
                          <button
                            type="button"
                            class="button button--small"
                            aria-pressed={row.paused}
                            disabled={!canEdit}
                            onClick={() => row.id !== null && props.onPause(row.id, !row.paused)}
                          >
                            {row.paused ? buildText.resume : buildText.pause}
                          </button>
                          <button
                            type="button"
                            class="button button--small"
                            disabled={!canEdit}
                            title={refundOf(row)}
                            onClick={() => row.id !== null && props.onCancel(row.id)}
                          >
                            {buildText.cancel}
                          </button>
                          <span class="hint build__refund">{refundOf(row)}</span>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      {cancelled.length > 0 && (
        <section class="build__cancelled">
          <h4>{buildText.cancelledTitle}</h4>
          <ul>
            {cancelled.map((c) => (
              <li key={c.id}>
                {c.label}:{' '}
                {Object.keys(c.refund).length === 0
                  ? buildText.cancelNoRefund
                  : buildText.refund(materialsText(c.refund))}{' '}
                <button
                  type="button"
                  class="button button--small"
                  disabled={!canEdit}
                  onClick={() => props.onCancel(c.id)}
                >
                  {buildText.undoCancel}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
